import {
  RISK_MODEL_IN_USE_CODE,
  type RiskModel,
  type RiskModelInUseResponse,
} from "@repo/shared/types";
import { Copy, Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { Switch } from "@/components/ui/switch";
import {
  rowControl,
  Table,
  TableActionsCell,
  TableActionsHead,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api/_client";
import * as riskModelsApi from "@/lib/api/risk-models";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import { useDeleteRiskModel, useToggleRiskModelActive } from "@/lib/hooks/use-risk-models";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./risk-models-table.i18n";

type RiskModelsTableProps = {
  models: RiskModel[];
  onEdit: (model: RiskModel) => void;
  onClone: (model: RiskModel) => void;
};

export function RiskModelsTable({ models, onEdit, onClone }: RiskModelsTableProps) {
  const localize = useLocalize();
  const t = useT(T);

  const deleteMutation = useDeleteRiskModel();
  const toggleMutation = useToggleRiskModelActive();

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingModelId, setDeletingModelId] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  // QL-168 — a model the server would refuse to delete shows why on the
  // Delete click, before the confirmation.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: riskModelsApi.checkDelete,
    onAllowed: (modelId) => {
      setDeletingModelId(modelId);
      setConfirmDeleteOpen(true);
    },
    onBlocked: (reason) =>
      reason
        ? showAlert(t.inUseTitle, reason, "destructive")
        : showAlert(t.error, t.failedToDelete, "destructive"),
  });

  const handleDeleteConfirm = async () => {
    if (!deletingModelId) return;
    try {
      await deleteMutation.mutateAsync(deletingModelId);
    } catch (err) {
      const body =
        err instanceof ApiError ? (err.body as Partial<RiskModelInUseResponse> | null) : null;
      const inUse = body?.code === RISK_MODEL_IN_USE_CODE;
      showAlert(
        inUse ? t.inUseTitle : t.error,
        err instanceof ApiError ? err.message : t.failedToDelete,
        "destructive",
      );
    }
    setDeletingModelId(null);
  };

  const handleToggleActive = async (model: RiskModel) => {
    try {
      await toggleMutation.mutateAsync(model.id);
    } catch (err) {
      showAlert(t.error, err instanceof ApiError ? err.message : t.failedToToggle, "destructive");
    }
  };

  if (models.length === 0) {
    return (
      <div className="text-center py-12 border rounded-lg">
        <p className="text-muted-foreground">{t.noModels}</p>
      </div>
    );
  }

  return (
    <>
      <Table variant="navigation">
        <TableHeader>
          <TableRow>
            <TableHead>{t.name}</TableHead>
            <TableHead className="w-20 text-right">{t.active}</TableHead>
            <TableActionsHead>{t.actions}</TableActionsHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {models.map((model) => (
            <TableRow key={model.id}>
              <TableCellLink className="font-medium" to={`/settings/risk-models/${model.id}`}>
                {localize(model.nameI18n)}
              </TableCellLink>
              <TableCell className={cn("text-right", rowControl)}>
                <Switch
                  aria-label={`${t.active}: ${localize(model.nameI18n)}`}
                  checked={model.isActive}
                  onCheckedChange={() => handleToggleActive(model)}
                />
              </TableCell>
              <TableActionsCell>
                <Button size="icon" tooltip={t.edit} variant="ghost" onClick={() => onEdit(model)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  tooltip={t.duplicate}
                  variant="ghost"
                  onClick={() => onClone(model)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  size="icon"
                  tooltip={t.delete}
                  variant="ghost"
                  loading={checkingId === model.id}
                  onClick={() => requestDelete(model.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </TableActionsCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteTitle}
        description={t.deleteDescription}
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDeleteConfirm}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
